import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Template, SupportedPageSize } from "../types/template.types";
import UploadFiles from "./upload-files.component";
import SelectTemplatePageSize from "./select-template-pagesize.component";
import { InlineSpinner } from "./inline-spinner";
import { useTemplateService } from "../services/template.service";

interface TemplateUploadsProps {
  template: Template;
  refreshTemplateInfo: () => void;
}
export default function TemplateUploads({ template, refreshTemplateInfo }: TemplateUploadsProps) {
  const { updateTemplate } = useTemplateService();
  const [processing, setProcessing] = useState(false);
  const [pageSize, setPageSize] = useState<SupportedPageSize>(SupportedPageSize.Unknown);
  const [showValidationError, setShowValidationError] = useState(false);
  const [areRowNumbersContinuous, setAreRowNumbersContinuous] = useState(false);
  const [isVariableNumberOfRows, setIsVariableNumberOfRows] = useState(false);
  const [isSingleSided, setIsSingleSided] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaveMessage, setSettingsSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(
    null
  );

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;

    if (value === "") {
      setPageSize(SupportedPageSize.Unknown);
    } else {
      setPageSize(value as SupportedPageSize);
    }
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isPageSizeValid = pageSize !== SupportedPageSize.Unknown;

    if (!isPageSizeValid) {
      setShowValidationError(true);
      e.target.value = "";
      return;
    }
    setShowValidationError(false);
  };

  function mapPageSizeValue(value: number | string): SupportedPageSize {
    switch (value) {
      case 1:
      case "Letter":
        return SupportedPageSize.Letter;
      case 2:
      case "Legal":
        return SupportedPageSize.Legal;
      default:
        return SupportedPageSize.Unknown;
    }
  }

  useEffect(() => {
    const mapped = mapPageSizeValue(template.pageSize);
    setPageSize(mapped);
  }, [template.pageSize]);

  useEffect(() => {
    setAreRowNumbersContinuous(template.areRowNumbersContinuous || false);
    setIsVariableNumberOfRows(template.isVariableNumberOfRows || false);
    setIsSingleSided(template.isSingleSided || false);
  }, [template.areRowNumbersContinuous, template.isVariableNumberOfRows, template.isSingleSided]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setSettingsSaveMessage(null);
    try {
      await updateTemplate(
        template.id.toString(),
        template.name,
        template.abbreviation,
        template.usStateId,
        areRowNumbersContinuous,
        isVariableNumberOfRows,
        isSingleSided
      );
      setSettingsSaveMessage({ type: "success", text: "Template settings saved successfully!" });
      refreshTemplateInfo();
      // Clear success message after 3 seconds
      setTimeout(() => setSettingsSaveMessage(null), 3000);
    } catch (error) {
      setSettingsSaveMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Failed to save template settings",
      });
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="mt-4">
      <h3>Upload Template Form</h3>

      <h6>Template Page Size</h6>
      <SelectTemplatePageSize onPageSizeChange={handlePageSizeChange} selectedPageSize={pageSize} />
      <h6>Template Options</h6>

      <div className="form-group mb-3">
        {/* <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="areRowNumbersContinuous"
            checked={areRowNumbersContinuous}
            onChange={(e) => setAreRowNumbersContinuous(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="areRowNumbersContinuous">
            Are Row Numbers Continuous
          </label>
        </div> */}
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isVariableNumberOfRows"
            checked={isVariableNumberOfRows}
            onChange={(e) => setIsVariableNumberOfRows(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="isVariableNumberOfRows">
            Is Variable Number of Rows
          </label>
        </div>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isSingleSided"
            checked={isSingleSided}
            onChange={(e) => setIsSingleSided(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="isSingleSided">
            Is Single Sided
          </label>
        </div>
      </div>

      <div className="mb-3">
        <button className="btn btn-sw me-3" onClick={handleSaveSettings} disabled={savingSettings || processing}>
          {savingSettings ? "Saving..." : "Save Settings"}
        </button>
        {settingsSaveMessage && (
          <div className={`alert alert-${settingsSaveMessage.type === "success" ? "success" : "danger"} mt-2`}>
            {settingsSaveMessage.text}
          </div>
        )}
      </div>

      {showValidationError && (
        <div className="text-danger mt-2">Please select a page size before uploading the template.</div>
      )}

      <h6>Template File</h6>
      <UploadFiles
        uploadUrlPath={`upload/templates?templateId=${template.id}&pageSize=${pageSize}&areRowNumbersContinuous=${areRowNumbersContinuous}&isVariableNumberOfRows=${isVariableNumberOfRows}&isSingleSided=${isSingleSided}`}
        fileTypes=".pdf, .tif, .tiff, .zip"
        isSynchronousUpload={true}
        onUploadProcessing={() => {
          setProcessing(true);
          setShowValidationError(false);
        }}
        onUploadComplete={() => {
          refreshTemplateInfo();
          setProcessing(false);
        }}
        onUploadError={() => setProcessing(false)}
        onFileSelected={handleFileSelected}
      />

      {processing && (
        <div>
          <InlineSpinner />
          <span className="small">Processing file...</span>
        </div>
      )}
      {template?.fileName ? (
        <>
          {!processing && (
            <>
              <span className="small">
                {`${template?.fileName} uploaded by ${template?.uploadedBy} on ${template?.uploadedOn}.`}
              </span>
              <div className="d-flex mt-3">
                <Link to={`/admin/templates/${template.id}/table-review`} className="btn btn-sw me-3">
                  Table Review
                </Link>

                <Link to={`/admin/templates/${template.id}/field-identification`} className="btn btn-sw me-3">
                  Identify Fields
                </Link>

                <Link to={`/admin/templates/${template.id}/column-identification`} className="btn btn-sw me-3">
                  Identify Columns
                </Link>

                <Link to={`/admin/templates/${template.id}/ignored-words`} className="btn btn-sw">
                  Ignored Words
                </Link>
              </div>
              {template?.fieldsModifiedBy && template?.fieldsModifiedOn && (
                <div>
                  <span className="small">
                    {`Fields were identified by ${template?.fieldsModifiedBy} on ${template?.fieldsModifiedOn}.`}
                  </span>
                </div>
              )}

              {template?.columnsModifiedBy && template?.columnsModifiedOn && (
                <div>
                  <span className="small">
                    {`Columns were identified by ${template?.columnsModifiedBy} on ${template?.columnsModifiedOn}.`}
                  </span>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <span className="small">No template uploaded</span>
      )}
    </div>
  );
}
